/**
 * Optimized Three.js scene with spatial hashing
 * Replaces O(n²) particle connections with O(1) lookups
 */

import * as THREE from 'three';
import { SpatialHash, RAFLoop, lerp, clamp } from '../core/utils.js';
import { getErrorBoundary } from '../core/errorBoundary.js';

export class ParticleScene {
  #scene;
  #camera;
  #renderer;
  #particles;
  #lines;
  #shapes = [];
  #spatialHash;
  #rafLoop;
  #mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };
  #scroll = { y: 0, targetY: 0 };
  #time = 0;
  #config;
  #isDestroyed = false;

  constructor(canvas, config = {}) {
    this.#config = {
      particleCount: 1200,  // Reduced from 2500 for mobile perf
      maxConnections: 150,
      connectionDistance: 4.5,
      cellSize: 5,
      ...config
    };

    this.#init(canvas);
  }

  #init(canvas) {
    const errorBoundary = getErrorBoundary();
    
    try {
      // Renderer
      this.#renderer = new THREE.WebGLRenderer({ 
        canvas, 
        antialias: false,  // Disabled for performance
        alpha: true,
        powerPreference: 'high-performance'
      });
      
      const dpr = Math.min(window.devicePixelRatio, 1.5);  // Cap DPR
      this.#renderer.setPixelRatio(dpr);
      this.#renderer.setSize(window.innerWidth, window.innerHeight);
      this.#renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.#renderer.toneMappingExposure = 1.0;

      // Scene
      this.#scene = new THREE.Scene();

      // Camera
      this.#camera = new THREE.PerspectiveCamera(
        60, 
        window.innerWidth / window.innerHeight, 
        0.1, 
        1000
      );
      this.#camera.position.set(0, 0, 30);

      // Particles
      this.#createParticles();
      
      // Connecting lines
      this.#createLines();
      
      // Geometric shapes
      this.#createShapes();

      // Spatial hash for O(1) neighbor lookup
      this.#spatialHash = new SpatialHash(this.#config.cellSize);

      // RAF loop with frame skipping
      this.#rafLoop = new RAFLoop(this.#animate.bind(this), { fps: 60 });

      // Resize observer
      this.#setupResize();

    } catch (error) {
      console.error('[ParticleScene] Init error:', error);
      errorBoundary?.show();
    }
  }

  #createParticles() {
    const count = this.#config.particleCount;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    this.velocities = [];

    const goldPalette = [
      [0.83, 0.68, 0.21],  // #d4af37
      [0.96, 0.84, 0.43],  // #f5d76e
      [0.72, 0.53, 0.04],  // #b8860b
      [1.0,  0.92, 0.65],  // near-white gold
      [0.48, 0.36, 0.0],   // dark gold
    ];

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 60;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 60;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 40 - 5;

      const c = goldPalette[Math.floor(Math.random() * goldPalette.length)];
      const dark = Math.random() < 0.4;
      colors[i * 3] = dark ? c[0] * 0.15 : c[0];
      colors[i * 3 + 1] = dark ? c[1] * 0.15 : c[1];
      colors[i * 3 + 2] = dark ? c[2] * 0.05 : c[2];

      sizes[i] = Math.random() * 2 + 0.5;
      
      this.velocities.push({
        x: (Math.random() - 0.5) * 0.008,
        y: (Math.random() - 0.5) * 0.008,
        z: (Math.random() - 0.5) * 0.004
      });
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMouse: { value: new THREE.Vector2(0, 0) },
        uScroll: { value: 0 },
        uPixelRatio: { value: this.#renderer.getPixelRatio() }
      },
      vertexShader: this.#getVertexShader(),
      fragmentShader: this.#getFragmentShader(),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this.#particles = new THREE.Points(geometry, material);
    this.#scene.add(this.#particles);
  }

  #getVertexShader() {
    return `
      attribute float size;
      attribute vec3 color;
      varying vec3 vColor;
      varying float vAlpha;
      uniform float uTime;
      uniform vec2 uMouse;
      uniform float uScroll;
      uniform float uPixelRatio;

      void main() {
        vColor = color;
        vec3 pos = position;
        
        // Gentle wave animation
        pos.x += sin(uTime * 0.25 + position.y * 0.5) * 0.35;
        pos.y += cos(uTime * 0.18 + position.x * 0.3) * 0.35;
        pos.z += sin(uTime * 0.1 + position.x * 0.2) * 0.2;
        pos.y += uScroll * 0.015;

        // Mouse repulsion (optimized)
        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
        vec2 screenPos = mvPosition.xy / mvPosition.w;
        float dist = distance(screenPos, uMouse * 2.0);
        float repulsion = smoothstep(1.5, 0.0, dist) * 1.5;
        pos.x += (screenPos.x - uMouse.x * 2.0) * repulsion * 0.3;
        pos.y += (screenPos.y - uMouse.y * 2.0) * repulsion * 0.3;

        mvPosition = modelViewMatrix * vec4(pos, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        gl_PointSize = size * uPixelRatio * (200.0 / -mvPosition.z);

        vAlpha = smoothstep(0.0, 0.3, size / 2.0) * (0.6 + sin(uTime + position.x) * 0.2);
      }
    `;
  }

  #getFragmentShader() {
    return `
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        float d = length(gl_PointCoord - 0.5);
        if (d > 0.5) discard;
        float alpha = smoothstep(0.5, 0.05, d) * vAlpha;
        gl_FragColor = vec4(vColor, alpha);
      }
    `;
  }

  #createLines() {
    const maxLines = this.#config.maxConnections;
    const positions = new Float32Array(maxLines * 6);
    const colors = new Float32Array(maxLines * 6);
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const material = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.1,
      blending: THREE.AdditiveBlending
    });

    this.#lines = new THREE.LineSegments(geometry, material);
    this.#lines.frustumCulled = false;  // Always render
    this.#scene.add(this.#lines);
  }

  #createShapes() {
    const shapeGroup = new THREE.Group();
    const geometries = [
      new THREE.IcosahedronGeometry(2, 0),     // Reduced detail
      new THREE.OctahedronGeometry(1.5, 0),
      new THREE.TorusGeometry(1.2, 0.3, 6, 16),  // Reduced segments
      new THREE.TetrahedronGeometry(1.8, 0),
      new THREE.TorusKnotGeometry(1, 0.3, 32, 4, 2, 3),  // Reduced segments
    ];

    const palette = [0xd4af37, 0xf5d76e, 0xb8860b, 0x7a5c00];

    for (let i = 0; i < 6; i++) {  // Reduced from 10
      const geo = geometries[i % geometries.length];
      const mat = new THREE.MeshBasicMaterial({
        color: palette[i % palette.length],
        transparent: true,
        opacity: 0.05,
        wireframe: true
      });
      
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(
        (Math.random() - 0.5) * 55,
        (Math.random() - 0.5) * 55,
        (Math.random() - 0.5) * 20 - 10
      );
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
      
      this.#shapes.push({
        mesh,
        rotSpeed: {
          x: (Math.random() - 0.5) * 0.003,
          y: (Math.random() - 0.5) * 0.003
        },
        floatOffset: Math.random() * Math.PI * 2,
        floatSpeed: 0.2 + Math.random() * 0.3
      });
      
      shapeGroup.add(mesh);
    }

    this.#scene.add(shapeGroup);
  }

  #setupResize() {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      this.#camera.aspect = width / height;
      this.#camera.updateProjectionMatrix();
      this.#renderer.setSize(width, height);
      
      if (this.#particles?.material?.uniforms?.uPixelRatio) {
        this.#particles.material.uniforms.uPixelRatio.value = 
          Math.min(window.devicePixelRatio, 1.5);
      }
    };

    // Throttled resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);
    }, { passive: true });
  }

  #animate() {
    if (this.#isDestroyed) return;

    this.#time += 0.016;

    // Smooth mouse interpolation
    this.#mouse.x = lerp(this.#mouse.x, this.#mouse.targetX, 0.05);
    this.#mouse.y = lerp(this.#mouse.y, this.#mouse.targetY, 0.05);
    this.#scroll.y = lerp(this.#scroll.y, this.#scroll.targetY, 0.08);

    // Update uniforms
    if (this.#particles?.material?.uniforms) {
      this.#particles.material.uniforms.uTime.value = this.#time;
      this.#particles.material.uniforms.uMouse.value.set(this.#mouse.x, this.#mouse.y);
      this.#particles.material.uniforms.uScroll.value = this.#scroll.y;
    }

    // Update particle positions
    const positions = this.#particles.geometry.attributes.position.array;
    const count = this.#config.particleCount;

    // Rebuild spatial hash every 3rd frame for performance
    if (Math.floor(this.#time * 60) % 3 === 0) {
      this.#spatialHash.clear();
      
      for (let i = 0; i < count; i++) {
        // Move particle
        positions[i * 3] += this.velocities[i].x;
        positions[i * 3 + 1] += this.velocities[i].y;
        positions[i * 3 + 2] += this.velocities[i].z;

        // Wrap around
        if (positions[i * 3] > 30) positions[i * 3] = -30;
        if (positions[i * 3] < -30) positions[i * 3] = 30;
        if (positions[i * 3 + 1] > 30) positions[i * 3 + 1] = -30;
        if (positions[i * 3 + 1] < -30) positions[i * 3 + 1] = 30;

        // Insert to spatial hash
        this.#spatialHash.insert({
          x: positions[i * 3],
          y: positions[i * 3 + 1],
          z: positions[i * 3 + 2]
        }, i);
      }
      
      this.#particles.geometry.attributes.position.needsUpdate = true;
      
      // Update connections using spatial hash - O(n) instead of O(n²)
      this.#updateConnectionsOptimized(positions);
    }

    // Animate shapes
    this.#shapes.forEach(s => {
      s.mesh.rotation.x += s.rotSpeed.x;
      s.mesh.rotation.y += s.rotSpeed.y;
      s.mesh.position.y += Math.sin(this.#time * s.floatSpeed + s.floatOffset) * 0.005;
    });

    // Camera follow mouse (subtle)
    this.#camera.position.x = this.#mouse.x * 1;
    this.#camera.position.y = this.#mouse.y * 0.8;
    this.#camera.lookAt(0, 0, 0);

    // Render
    this.#renderer.render(this.#scene, this.#camera);
  }

  #updateConnectionsOptimized(positions) {
    const maxLines = this.#config.maxConnections;
    const connectionDist = this.#config.connectionDistance;
    const linePositions = this.#lines.geometry.attributes.position.array;
    const lineColors = this.#lines.geometry.attributes.color.array;
    
    let lineIdx = 0;
    const count = this.#config.particleCount;
    const maxChecks = Math.min(count, 150);  // Limit checks per frame

    for (let i = 0; i < maxChecks && lineIdx < maxLines; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];

      // Query spatial hash for neighbors
      const neighbors = this.#spatialHash.query(x, y, z, connectionDist);
      
      for (const neighbor of neighbors) {
        if (neighbor.index <= i) continue;  // Avoid duplicates
        if (lineIdx >= maxLines) break;

        const j = neighbor.index;
        const a = 1 - neighbor.distance / connectionDist;
        
        // Line positions
        linePositions[lineIdx * 6] = x;
        linePositions[lineIdx * 6 + 1] = y;
        linePositions[lineIdx * 6 + 2] = z;
        linePositions[lineIdx * 6 + 3] = positions[j * 3];
        linePositions[lineIdx * 6 + 4] = positions[j * 3 + 1];
        linePositions[lineIdx * 6 + 5] = positions[j * 3 + 2];

        // Gold gradient color
        lineColors[lineIdx * 6] = 0.83 * a;
        lineColors[lineIdx * 6 + 1] = 0.68 * a;
        lineColors[lineIdx * 6 + 2] = 0.21 * a;
        lineColors[lineIdx * 6 + 3] = 0.96 * a;
        lineColors[lineIdx * 6 + 4] = 0.84 * a;
        lineColors[lineIdx * 6 + 5] = 0.43 * a;

        lineIdx++;
      }
    }

    this.#lines.geometry.setDrawRange(0, lineIdx * 2);
    this.#lines.geometry.attributes.position.needsUpdate = true;
    this.#lines.geometry.attributes.color.needsUpdate = true;
  }

  // Public API
  setMouse(x, y) {
    this.#mouse.targetX = x;
    this.#mouse.targetY = y;
  }

  setScroll(y) {
    this.#scroll.targetY = y;
  }

  start() {
    this.#rafLoop?.start();
  }

  stop() {
    this.#rafLoop?.stop();
  }

  destroy() {
    this.#isDestroyed = true;
    this.stop();
    
    // Dispose geometries and materials
    this.#particles?.geometry?.dispose();
    this.#particles?.material?.dispose();
    this.#lines?.geometry?.dispose();
    this.#lines?.material?.dispose();
    
    this.#shapes.forEach(s => {
      s.mesh.geometry?.dispose();
      s.mesh.material?.dispose();
    });

    this.#renderer?.dispose();
    this.#spatialHash?.clear();
  }
}
